import { read, TABLE } from '../data/db';
import { settings } from '../data/repos';
import { TEAM_SHAPE_FIELDS } from '../types';
import type { SettingKey, TeamShape } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';

/**
 * B spec §4.5 — **the settings facade** (build unit B-4, F-5). What B's More tab (unit B-19)
 * consumes: the service credential paste field, the theme choice, the lock nudge minutes.
 *
 * The A convention, unchanged: `load*` is one readonly transaction; the write dispatches exactly
 * one command and returns `DispatchResult`; `onCommitted` is not offered because nothing here is
 * a capture ack — the More tab re-reads after a successful write, the imperative-re-read
 * convention every screen follows.
 *
 * ── The scope is one table, and that is a mechanism ──────────────────────────
 *
 * Settings reference no other row and nothing references them. Every scope below is
 * `[TABLE.settings]` alone, so "a preferences screen changes nothing about any match" is a
 * runtime property (an out-of-scope read throws) on top of the compile-time one (`SettingsOp`
 * names one table). Unknown keys are refused by the engine (`ERR_SETTING_KEY`), returned as a
 * value like every rejection.
 */

/**
 * Stored settings by key. A key that was never set is absent — the consuming surface supplies
 * its own default (light theme, the config nudge default), the same contract an import leaves
 * behind. Values are `unknown` at this grain: the engine's contract is the closed *key* space,
 * and the More tab owns each value's vocabulary.
 */
export type SettingsView = Readonly<Partial<Record<SettingKey, unknown>>>;

/** The whole settings table, one readonly transaction — a handful of rows at most. */
export function loadSettings(): Promise<SettingsView> {
  return read(
    [TABLE.settings],
    async () => {
      const rows = await settings.listSettings();
      const view: Partial<Record<SettingKey, unknown>> = {};
      for (const row of rows) view[row.key] = row.value;
      return view;
    },
    'loadSettings',
  );
}

/**
 * B §4.2 (F-5) — write one setting, create-or-replace. The typed `key` keeps in-repo callers
 * inside the closed union at compile time; the handler re-checks at runtime, so a key that
 * crossed a UI boundary as a string comes back as `ERR_SETTING_KEY` with nothing committed.
 */
export function updateSetting(key: SettingKey, value: unknown): Promise<DispatchResult> {
  return dispatch({ type: 'UpdateSetting', key, value });
}

// ── §15.4 — the team shape, the one structured setting value ─────────────────

/** §3.7.38 — a shape with nothing recorded. Every field blank, never written. */
export const EMPTY_TEAM_SHAPE: TeamShape = {
  outOfPossession: '',
  inPossession: '',
  transition: '',
  updatedAt: 0,
};

/**
 * **Pre-mortem PM-4's mechanism, and it is a `Record`, not a comment.**
 *
 * `Record<keyof TeamShape, …>` requires an entry for **every** field of the shape, so a fifth
 * field added to `TeamShape` without a parse arm is a **compile error** rather than a field that
 * silently never round-trips. That completeness is what replaces the `Partitions<>` guarantee a
 * dedicated `teamShape` table would have brought — the cost ruling R-4 named out loud.
 *
 * `'text'` is a user-authored string where `''` means *not recorded*; `'stamp'` is R-3's
 * `updatedAt`, epoch milliseconds, `0` meaning *never written*.
 */
const TEAM_SHAPE_FIELD_KINDS: Readonly<Record<keyof TeamShape, 'text' | 'stamp'>> = {
  outOfPossession: 'text',
  inPossession: 'text',
  transition: 'text',
  updatedAt: 'stamp',
};

/**
 * §15.4's **parse-at-read** guard: total, and it never throws.
 *
 * A corrupt, foreign or absent value degrades to {@link EMPTY_TEAM_SHAPE}, so My Team says *"No
 * shape recorded"* and the payload omits the block. That is the shape `domain/commands/settings.ts`
 * prescribes — *"the row stores `value: unknown` and the engine's contract is the closed key space;
 * what a theme may be called or a nudge may number is the consuming surface's business"*.
 *
 * Driven by {@link TEAM_SHAPE_FIELD_KINDS} rather than by three hand-written reads, so the
 * exhaustiveness is the compiler's rather than the reader's.
 *
 * Fields are read **independently**: one corrupt field does not discard the two the user did fill
 * in, because losing a season's tactical note to one bad timestamp would be a worse failure than
 * showing a partial shape.
 */
export function parseTeamShape(value: unknown): TeamShape {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return EMPTY_TEAM_SHAPE;
  }
  const record = value as Record<string, unknown>;
  const parsed: { -readonly [K in keyof TeamShape]: TeamShape[K] } = { ...EMPTY_TEAM_SHAPE };
  for (const field of TEAM_SHAPE_FIELDS) {
    const raw = record[field];
    if (TEAM_SHAPE_FIELD_KINDS[field] === 'stamp') {
      if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0) parsed.updatedAt = raw;
    } else if (typeof raw === 'string' && field !== 'updatedAt') {
      parsed[field] = raw;
    }
  }
  return parsed;
}

/**
 * Whether a shape says anything at all.
 *
 * **Non-empty, not merely present.** `parseTeamShape` degrades a corrupt or foreign value to
 * {@link EMPTY_TEAM_SHAPE}, and an empty shape is nothing an import can take away — so §15.6's
 * replace-all warning must turn on this rather than on the row's existence, or it would raise a
 * false alarm about losing something that was never there. `updatedAt` deliberately does **not**
 * count: a stamp with three blank fields is a write, not a shape.
 */
export function isRecordedShape(shape: TeamShape): boolean {
  return (
    shape.outOfPossession.trim() !== '' ||
    shape.inPossession.trim() !== '' ||
    shape.transition.trim() !== ''
  );
}

/**
 * §15.4 — **a narrow read, not `loadSettings()`.**
 *
 * The Coach Claude workflow reads the shape through this and only this: a bare `loadSettings()`
 * would pull `backupCredential` — the value that authenticates the backup service — into the same
 * memory as the payload builder, for no reason but convenience.
 */
export function loadTeamShape(): Promise<TeamShape> {
  return read(
    [TABLE.settings],
    async () => parseTeamShape((await settings.getSetting('teamShape'))?.value),
    'loadTeamShape',
  );
}

/**
 * §17.3 — **one `UpdateSetting`, one transaction, the whole shape or none of it.**
 *
 * Three keys would be three dispatches with no atomicity across them, and an iOS kill between two
 * of them would leave a profile whose in-possession shape saved and whose out-of-possession shape
 * did not. §3.7.38 states this is *one* fact; one key is what makes that true at runtime rather
 * than only in the prose.
 *
 * `updatedAt` is stamped here rather than taken from the caller, so §17.1's freshness line cannot
 * be back-dated by a stale sheet (ruling R-3's purpose).
 */
export function saveTeamShape(
  shape: Omit<TeamShape, 'updatedAt'>,
  at: number = Date.now(),
): Promise<DispatchResult> {
  const value: TeamShape = {
    outOfPossession: shape.outOfPossession.trim(),
    inPossession: shape.inPossession.trim(),
    transition: shape.transition.trim(),
    updatedAt: at,
  };
  return updateSetting('teamShape', value);
}
